import { useEffect, useRef, useState } from "react";

interface Item {
	id: string | number;
	fileBlob: Blob;
}

interface UseIndexedDB {
	addOrUpdate: (item: Item) => Promise<boolean>;
	getItem: (id: string | number) => Promise<Item | undefined>;
	deleteItem: (id: string | number) => Promise<boolean>;
}

const useIndexedDB = (dbName: string, storeName: string): UseIndexedDB => {
	const [db, setDb] = useState<IDBDatabase | null>(null);
	const dbPromiseRef = useRef<Promise<IDBDatabase> | null>(null);

	useEffect(() => {
		const openDB = () => {
			return new Promise<IDBDatabase>((resolve, reject) => {
				const request = indexedDB.open(dbName, 1);

				request.onupgradeneeded = (event) => {
					const db = (event.target as IDBOpenDBRequest).result as IDBDatabase;
					if (!db.objectStoreNames.contains(storeName)) {
						db.createObjectStore(storeName, { keyPath: "id" });
					}
				};

				request.onsuccess = (event) => {
					resolve((event.target as IDBOpenDBRequest).result as IDBDatabase);
				};

				request.onerror = (event) => {
					reject((event.target as IDBOpenDBRequest).error);
				};

				request.onblocked = () => {
					console.warn("IndexedDB open blocked - close other tabs");
				};
			});
		};

		dbPromiseRef.current = openDB();
		dbPromiseRef.current.then(setDb).catch(console.error);

		return () => {
			// Close the database connection using the promise ref
			dbPromiseRef.current
				?.then((database) => {
					database.close();
				})
				.catch(() => {
					// Ignore errors during cleanup
				});
			// Clear the promise ref
			dbPromiseRef.current = null;
			// Reset the db state
			setDb(null);
		};
	}, [dbName, storeName]);

	const addOrUpdate = async (item: Item): Promise<boolean> => {
		try {
			// Wait for DB to be ready
			const database = await dbPromiseRef.current;
			if (!database) {
				return false;
			}

			return new Promise((resolve) => {
				try {
					const transaction = database.transaction([storeName], "readwrite");
					const store = transaction.objectStore(storeName);
					const request = store.put(item);

					request.onsuccess = () => {
						resolve(true);
					};

					request.onerror = () => {
						resolve(false);
					};

					transaction.onerror = () => {
						resolve(false);
					};

					transaction.onabort = () => {
						resolve(false);
					};
				} catch (error) {
					console.error("Error in addOrUpdate:", error);
					resolve(false);
				}
			});
		} catch (error) {
			return false;
		}
	};

	const getItem = async (id: string | number): Promise<Item | undefined> => {
		try {
			// Wait for DB to be ready
			const database = await dbPromiseRef.current;
			if (!database) {
				return undefined;
			}

			return new Promise((resolve) => {
				try {
					const transaction = database.transaction([storeName], "readonly");
					const store = transaction.objectStore(storeName);
					const request = store.get(id);

					request.onsuccess = () => {
						resolve(request.result as Item | undefined);
					};

					request.onerror = () => {
						resolve(undefined);
					};

					transaction.onerror = () => {
						resolve(undefined);
					};
				} catch (error) {
					console.error("Error in getItem:", error);
					resolve(undefined);
				}
			});
		} catch (error) {
			return undefined;
		}
	};

	const deleteItem = async (id: string | number): Promise<boolean> => {
		try {
			// Wait for DB to be ready
			const database = await dbPromiseRef.current;
			if (!database) {
				return false;
			}

			return new Promise((resolve) => {
				try {
					const transaction = database.transaction([storeName], "readwrite");
					const store = transaction.objectStore(storeName);
					const request = store.delete(id);

					request.onsuccess = () => {
						resolve(true);
					};

					request.onerror = () => {
						resolve(false);
					};

					transaction.onerror = () => {
						resolve(false);
					};
				} catch (error) {
					console.error("Error in deleteItem:", error);
					resolve(false);
				}
			});
		} catch (error) {
			return false;
		}
	};

	return { addOrUpdate, getItem, deleteItem };
};

export default useIndexedDB;
